import { useState, useCallback, useRef, useEffect, useMemo, type ReactNode } from 'react'
import { Toaster } from '@/components/ui/sonner'
import { toast } from 'sonner'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Sidebar } from '@/components/sidebar/Sidebar'
import type { ActiveNav } from '@/components/sidebar/Sidebar'
import { SubSidebar } from '@/components/sidebar/SubSidebar'
import { WorkspaceWelcome } from '@/components/sidebar/WorkspaceWelcome'
import { UploadModal } from '@/components/sidebar/UploadModal'
import { SettingsModal } from '@/components/settings/SettingsModal'
import { WorkspaceSettingsModal } from '@/components/settings/WorkspaceSettingsModal'
import { InviteModal } from '@/components/settings/InviteModal'
import { Workbench } from '@/components/workbench/Workbench'
import { RightPanel } from '@/components/evidence/RightPanel'
import type { RightPanelMode } from '@/components/evidence/RightPanel'
import { libraryItems as initialLibrary } from '@/data/mock'
import type { Draft, LibraryItem, VeevaSubtype } from '@/data/mock'
import type { ChatResponse, AuditInitResponse, AuditStatusResponse, AtomicBreakdownEntry, ClaimRow, SourceListItem, DraftResponse, JSONContent } from '@/types/api'
import { apiFetch } from '@/lib/apiFetch'
import { capture } from '@/lib/analytics'
import { useAuth } from '@/hooks/useAuth'
import { useOrgInfo } from '@/hooks/useOrgInfo'
import { invalidateBillingUsage } from '@/hooks/useBillingUsage'
import { SubscribeGate } from '@/components/SubscribeGate'
import { hasIntentionalLogout, clearIntentionalLogout, type AuthUser } from '@/lib/auth'
import { WorkspaceProvider, useWorkspace } from '@/contexts/WorkspaceContext'
import { ReviewPanelProvider } from '@/contexts/ReviewPanelContext'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'


// ── Shared claim-row → breakdown mapper ──────────────────────────────────────

function mapClaimsToBreakdown(claims: ClaimRow[]): AtomicBreakdownEntry[] {
  return claims.map((c) => ({
    claim: c.resolved_claim,
    status: c.status,
    reasoning: c.reasoning ?? undefined,
    suggested_edit: c.suggested_edit ?? undefined,
    evidences: c.evidences ?? undefined,
    scrutiny_flags: c.scrutiny_flags ?? undefined,
    scrutiny_findings: c.scrutiny_findings ?? undefined,
    review_state: c.review_state ?? undefined,
    claim_uuid: c.id,
    original_sentence: c.original_sentence,
  }))
}

// ── Authenticated app (rendered only when isAuthenticated is true) ───────────

function AuthenticatedApp({
  user,
  logout,
  isAuthenticated,
}: {
  user: AuthUser | null
  logout: () => void
  isAuthenticated: boolean
}) {
  const { workspaces, activeWorkspaceId, setActiveWorkspaceId, createWorkspace, renameWorkspace } = useWorkspace()

  const [activeDocId, setActiveDocId] = useState<string | null>(null)
  const [selectedClaimId, setSelectedClaimId] = useState<string | null>(null)
  const [selectedLibraryItemId, setSelectedLibraryItemId] = useState<string | null>(null)
  const [libraryItems, setLibraryItems] = useState<LibraryItem[]>(initialLibrary)
  const [supersededItems, setSupersededItems] = useState<LibraryItem[]>([])
  const [documents, setDocuments] = useState<Draft[]>([])
  const [activeNav, setActiveNav] = useState<ActiveNav>(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [wsSettingsOpen, setWsSettingsOpen] = useState(false)
  const [inviteOpen, setInviteOpen] = useState(false)

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(false)
  const [rightPanelMode, setRightPanelMode] = useState<RightPanelMode>('review')

  const [hasAuditResults, setHasAuditResults] = useState(false)
  const [isAuditing, setIsAuditing] = useState(false)
  const [isIncrementalResult, setIsIncrementalResult] = useState(false)
  const [highlightsVisible, setHighlightsVisible] = useState(true)
  const [focusedSourceId, setFocusedSourceId] = useState<string | null>(null)
  const [activeBreakdownIndices, setActiveBreakdownIndices] = useState<Set<number> | null>(null)

  // Surgery state (lifted from ReviewPanelContext for cross-component sync)
  const [reviewViewMode, setReviewViewMode] = useState<'triage' | 'surgery' | 'abbreviations'>('triage')
  const [surgeryClaimId, setSurgeryClaimId] = useState<string | null>(null)

  // Derive activeSurgeryClaimIndex directly — no callbacks, no effects
  const activeSurgeryClaimIndex = reviewViewMode === 'surgery' && surgeryClaimId !== null
    ? parseInt(surgeryClaimId, 10)
    : null

  // Per-draft concurrent audit polling: Map<draftId, auditId>
  const [pollingAudits, setPollingAudits] = useState<Map<string, string>>(new Map())
  const [progressMessage, setProgressMessage] = useState<string | null>(null)
  const [auditError, setAuditError] = useState<string | null>(null)
  const [workbenchKey, setWorkbenchKey] = useState(0)
  const [claimStatusOverrides, setClaimStatusOverrides] = useState<Map<string, string>>(new Map())
  // Per-claim hidden-source map (keyed by claim_uuid). Lifted from
  // ReviewPanelContext so Workbench can dispatch it into the source-registry
  // ProseMirror plugin via meta — drives reactive citation labels + bibliography.
  const [hiddenSourcesByClaimUuid, setHiddenSourcesByClaimUuid] = useState<Map<string, Set<string>>>(new Map())
  const [isStale, setIsStale] = useState(false)

  const [activeDocumentId, setActiveDocumentId] = useState<string | null>(null)

  // UUID → LibraryItem lookup for resolving human-readable source citations
  const sourceLookup = useMemo(() => {
    const map = new Map<string, LibraryItem>()
    libraryItems.forEach(item => map.set(item.id, item))
    return map
  }, [libraryItems])

  const [lastCompletedAuditId, setLastCompletedAuditId] = useState<string | null>(null)
  const [apiAuditResult, setApiAuditResult] = useState<(ChatResponse & { query: string }) | null>(null)

  const [focusedClaimIndices, setFocusedClaimIndices] = useState<number[]>([])

  const editorHelpersRef = useRef<{
    getText: () => string
    getJSON: () => JSONContent
    setContent: (content: JSONContent) => void
    setEditable: (editable: boolean) => void
    replaceText: (search: string, replace: string) => boolean
    removeCitationByClaimUuid: (claimUuid: string) => import('@/types/citation').CitationSnapshot | null
    insertCitationAt: (
      pos: number,
      attrs: { claimId: string; attachmentId?: string | null; sourceUuids: string; indices?: string; claimIndices?: string | null; sentenceHash?: string | null },
    ) => boolean
  } | null>(null)
  const editorGetTextRef = useRef<(() => string) | null>(null)

  // Draft auto-save state & refs
  const [activeDraftContent, setActiveDraftContent] = useState<JSONContent | null>(null)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
  const pendingContentRef = useRef<JSONContent | null>(null)
  const pendingTitleRef = useRef<string | null>(null)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const activeDocIdRef = useRef(activeDocId)
  useEffect(() => { activeDocIdRef.current = activeDocId })
  const prevDocIdRef = useRef<string | null>(null)
  const autoCreatingRef = useRef(false)
  const skipReloadRef = useRef(false)
  // Optimistic-concurrency token for the active draft. Updated by every
  // successful GET or PATCH; sent back as ``expected_version`` on the next
  // PATCH so the server can 409 us if someone else (or another tab) saved
  // in between. Ref, not state — setting it must not trigger renders.
  const draftVersionRef = useRef<number | null>(null)

  const auditCacheRef = useRef<Map<string, {
    apiAuditResult: (ChatResponse & { query: string }) | null
    hasAuditResults: boolean
    auditError: string | null
    lastCompletedAuditId: string | null
    isIncrementalResult: boolean
  }>>(new Map())

  // Poll GET /api/audits/{audit_id} every 2.5 s for ALL running audits (concurrent)
  useEffect(() => {
    if (pollingAudits.size === 0 || !isAuthenticated) return

    const intervalId = setInterval(async () => {
      const finished: string[] = []

      for (const [draftId, auditId] of pollingAudits) {
        try {
          const res = await apiFetch(`/api/audits/${auditId}`)
          if (!res.ok) {
            const err = await res.json().catch(() => ({}))
            throw new Error((err as { detail?: string }).detail ?? `HTTP ${res.status}`)
          }
          const data: AuditStatusResponse = await res.json()
          const status = data.audit.status
          const isActiveDraft = draftId === activeDocIdRef.current

          // Update progress message only for the active draft
          if (isActiveDraft) {
            setProgressMessage(data.audit.progress_message ?? null)
          }

          if (status === 'complete') {
            finished.push(draftId)
            // The audit just metered new checks server-side — refresh the
            // sidebar usage ring so it reflects the new count without a reload.
            invalidateBillingUsage()

            const atomicBreakdown = mapClaimsToBreakdown(data.claims)
            const completedResult = {
              original_text: data.audit.document_text_snapshot,
              atomic_breakdown: atomicBreakdown,
              abbreviations: data.audit.abbreviations ?? [],
              query: data.audit.document_text_snapshot,
            }

            if (isActiveDraft) {
              // 1. Fetch hydrated draft BEFORE setting React state, so the
              //    Workbench useEffect sees citation nodes on its first run.
              let hydratedContent: JSONContent | null = null
              let hasHydratedCitations = false
              if (data.audit.draft_id && editorHelpersRef.current) {
                try {
                  const draftRes = await apiFetch(`/api/drafts/${data.audit.draft_id}`)
                  if (draftRes.ok) {
                    const draft: DraftResponse = await draftRes.json()
                    if (draft.content) {
                      hydratedContent = draft.content
                      hasHydratedCitations = JSON.stringify(draft.content).includes('"type":"citation"')
                      const apiCitationCount = (JSON.stringify(draft.content).match(/"type":"citation"/g) || []).length
                      console.log('[audit-diag] /api/drafts hydrated content citation_count=', apiCitationCount)
                    }
                  }
                } catch { /* draft refresh failed — visualizeApiCitations will handle it */ }
              }

              // 2. Set editor content BEFORE triggering React state updates
              if (hydratedContent && editorHelpersRef.current) {
                editorHelpersRef.current.setContent(hydratedContent)
                setActiveDraftContent(hydratedContent)
                const editorJsonStr = JSON.stringify(editorHelpersRef.current.getJSON())
                const editorCitationCount = (editorJsonStr.match(/"type":"citation"/g) || []).length
                console.log('[audit-diag] editor after setContent citation_count=', editorCitationCount)
              }

              // 3. Now set React state (triggers Workbench useEffect)
              setIsIncrementalResult(hasHydratedCitations)
              setLastCompletedAuditId(data.audit.id)
              setApiAuditResult(completedResult)
              setHasAuditResults(true)
              setIsAuditing(false)
              editorHelpersRef.current?.setEditable(true)
              capture('triage_completed', {
                claim_count: atomicBreakdown.length,
                incremental: hasHydratedCitations,
              })
            }

            // Write to cache (for background drafts or future tab switches)
            auditCacheRef.current.set(draftId, {
              apiAuditResult: completedResult,
              hasAuditResults: true,
              auditError: null,
              lastCompletedAuditId: data.audit.id,
              isIncrementalResult: !!data.audit.content_json,
            })

            if (!isActiveDraft) {
              // Background draft: show a toast so user knows
              toast.success('Background audit completed', {
                description: 'Switch to the draft to see results.',
              })
            }
          } else if (status === 'failed') {
            finished.push(draftId)

            // Write failure to cache
            auditCacheRef.current.set(draftId, {
              apiAuditResult: auditCacheRef.current.get(draftId)?.apiAuditResult ?? null,
              hasAuditResults: false,
              auditError: data.audit.progress_message ?? 'Audit failed',
              lastCompletedAuditId: auditCacheRef.current.get(draftId)?.lastCompletedAuditId ?? null,
              isIncrementalResult: false,
            })

            if (isActiveDraft) {
              setIsAuditing(false)
              setAuditError(data.audit.progress_message ?? 'Audit failed')
              editorHelpersRef.current?.setEditable(true)
            } else {
              toast.error('Background audit failed')
            }
          }
        } catch (err) {
          finished.push(draftId)
          const isActiveDraft = draftId === activeDocIdRef.current
          if (isActiveDraft) {
            setIsAuditing(false)
            editorHelpersRef.current?.setEditable(true)
            toast.error(err instanceof Error ? err.message : 'Audit polling failed')
          } else {
            toast.error('Background audit polling failed')
          }
        }
      }

      // Remove finished audits from polling map
      if (finished.length > 0) {
        setPollingAudits(prev => {
          const next = new Map(prev)
          for (const id of finished) next.delete(id)
          return next
        })
      }
    }, 2500)

    return () => clearInterval(intervalId)
  }, [pollingAudits, isAuthenticated])

  function toLibraryItem(d: SourceListItem): LibraryItem {
    const stem = d.filename.replace(/\.pdf$/i, '')
    const effectiveShort = d.short_citation_override ?? d.short_citation
    const effectiveAma = d.ama_citation_override ?? d.ama_citation
    return {
      id: d.id,
      filename: d.filename,
      status: d.status,
      workspaceId: d.workspace_id,
      title: effectiveShort ?? effectiveAma ?? stem,
      fileSubtype: (d.file_subtype as VeevaSubtype) ?? 'Publication',
      shortCitation: d.short_citation ?? undefined,
      amaCitation: d.ama_citation ?? stem,
      shortCitationOverride: d.short_citation_override ?? undefined,
      amaCitationOverride: d.ama_citation_override ?? undefined,
      abstract: d.abstract ?? undefined,
      source: d.source ?? undefined,
      sourceDate: d.source_date ?? undefined,
      authors: d.authors ?? undefined,
      doi: d.doi ?? undefined,
      pmid: d.pmid ?? undefined,
      url: d.url ?? undefined,
      language: d.language ?? undefined,
      volume: d.volume ?? undefined,
      pageRange: d.page_range ?? undefined,
      meshTerms: d.mesh_terms ?? undefined,
      activeIngredients: d.active_ingredients ?? undefined,
      brand: d.brand ?? undefined,
      productName: d.product_name ?? undefined,
      companyName: d.company_name ?? undefined,
      therapeuticArea: d.therapeutic_area ?? undefined,
      isActiveInRag: d.is_active_in_rag ?? true,
      meshSource: (d.mesh_source as 'pubmed' | 'mesh_on_demand' | null) ?? undefined,
      supersededBy: d.superseded_by ?? undefined,
      supersededAt: d.superseded_at ?? undefined,
      supersededVia: (d.superseded_via as LibraryItem['supersededVia']) ?? undefined,
      originalWorkspaceId: d.original_workspace_id ?? undefined,
      enrichmentTelemetrySummary: (d.enrichment_telemetry_summary as LibraryItem['enrichmentTelemetrySummary']) ?? undefined,
    }
  }

  const refreshLibrary = useCallback(async () => {
    if (!activeWorkspaceId) return
    try {
      // Fetch both in parallel — superseded ids must be part of the
      // "known to backend" set so optimistic processing placeholders for
      // soft-deleted duplicates get cleared from libraryItems. Otherwise
      // they stay stuck with a spinning badge forever (the canonical
      // /api/sources response excludes superseded rows, but the local
      // optimistic items still match by id).
      const [activeResp, supersededResp] = await Promise.all([
        apiFetch(`/api/sources?workspace_id=${activeWorkspaceId}`),
        apiFetch(`/api/sources/superseded?workspace_id=${activeWorkspaceId}`),
      ])
      const activeData = (await activeResp.json()) as SourceListItem[]
      const supersededData = (await supersededResp.json()) as SourceListItem[]
      if (!Array.isArray(activeData) || !Array.isArray(supersededData)) return

      const freshItems = activeData.map(toLibraryItem)
      const supersededMapped = supersededData.map(toLibraryItem)
      const knownIds = new Set<string>([
        ...freshItems.map(i => i.id),
        ...supersededMapped.map(i => i.id),
      ])
      setLibraryItems(prev => {
        const localOnly = prev.filter(
          i => i.status === 'processing' && !knownIds.has(i.id)
        )
        return [...freshItems, ...localOnly]
      })
      setSupersededItems(supersededMapped)
    } catch {
      // Network errors here are non-fatal — next poll will retry
    }
  }, [activeWorkspaceId])

  // Fetch library items from DB whenever the active workspace changes
  useEffect(() => {
    refreshLibrary()
  }, [refreshLibrary])

  // Poll for enrichment completion while any items are still processing
  useEffect(() => {
    const hasProcessing = libraryItems.some(item => item.status === 'processing')
    if (!hasProcessing) return

    const intervalId = setInterval(() => {
      refreshLibrary()
    }, 5000)

    return () => clearInterval(intervalId)
  }, [libraryItems, refreshLibrary])

  // Fetch drafts from DB whenever the active workspace changes
  useEffect(() => {
    if (!activeWorkspaceId) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- workspace switch must reset doc selection before async fetch settles
    setActiveDocId(null)
    apiFetch(`/api/drafts?workspace_id=${activeWorkspaceId}`)
      .then(r => r.json())
      .then((data: DraftResponse[]) => {
        if (!Array.isArray(data)) return
        const mapped = data.map(d => ({
          id: d.id,
          title: d.title,
          lastModified: new Date(d.updated_at),
          createdAt: d.created_at,
          lastOpenedAt: d.updated_at,
          workspaceId: d.workspace_id,
        }))
        setDocuments(mapped)
        if (mapped.length > 0) {
          setActiveDocId(mapped[0].id)
        }
      })
      .catch(() => {})
  }, [activeWorkspaceId])

  // Load draft content when a draft is selected
  useEffect(() => {
    const prevDocId = prevDocIdRef.current
    prevDocIdRef.current = activeDocId

    // Clear any pending save timer
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
    }

    // Capture pending changes before clearing refs
    let flushBody: Record<string, unknown> | null = null
    if (prevDocId && (pendingContentRef.current || pendingTitleRef.current !== null)) {
      flushBody = {}
      if (pendingContentRef.current) {
        flushBody.content = pendingContentRef.current
        pendingContentRef.current = null
      }
      if (pendingTitleRef.current !== null) {
        flushBody.title = pendingTitleRef.current
        pendingTitleRef.current = null
      }
    }

    // Save outgoing draft's audit state into cache
    if (prevDocId) {
      auditCacheRef.current.set(prevDocId, {
        apiAuditResult,
        hasAuditResults,
        auditError,
        lastCompletedAuditId,
        isIncrementalResult,
      })
    }

    // Restore incoming draft's audit state from cache (or reset to neutral)
    const cached = activeDocId ? auditCacheRef.current.get(activeDocId) : undefined
    if (cached) {
      setApiAuditResult(cached.apiAuditResult)
      setHasAuditResults(cached.hasAuditResults)
      setAuditError(cached.auditError)
      setLastCompletedAuditId(cached.lastCompletedAuditId)
      setIsIncrementalResult(cached.isIncrementalResult ?? false)
    } else {
      setApiAuditResult(null)
      setHasAuditResults(false)
      setAuditError(null)
      setLastCompletedAuditId(null)
      setIsIncrementalResult(false)
    }
    // Restore isAuditing based on whether this draft has a running audit
    const incomingIsAuditing = activeDocId ? pollingAudits.has(activeDocId) : false
    setIsAuditing(incomingIsAuditing)
    setFocusedClaimIndices([])
    setReviewViewMode('triage')
    setSurgeryClaimId(null)
    setProgressMessage(incomingIsAuditing ? 'Audit in progress...' : null)

    setSaveStatus('idle')

    if (!activeDocId) {
      setActiveDraftContent(null)
      setWorkbenchKey(k => k + 1)
      return
    }

    if (skipReloadRef.current) {
      skipReloadRef.current = false
      return
    }

    let cancelled = false

    ;(async () => {
      // 1. Await flush PATCH for outgoing draft. No expected_version
      //    here: this is a switch-away flush, the user moved on, and
      //    forcing a 409 prompt when they can't see the draft anymore
      //    would be hostile. Falls back to legacy last-write-wins.
      if (prevDocId && flushBody) {
        try {
          const res = await apiFetch(`/api/drafts/${prevDocId}`, {
            method: 'PATCH',
            body: JSON.stringify(flushBody),
          })
          if (!res.ok) {
            toast.error('Failed to save previous draft')
          }
        } catch {
          toast.error('Error saving draft — please check your connection')
        }
      }

      if (cancelled) return

      // Reset the version token before fetching the incoming draft.
      // We don't want a stale value to leak across documents.
      draftVersionRef.current = null

      // 2. Fetch incoming draft from API (source of truth)
      try {
        const res = await apiFetch(`/api/drafts/${activeDocId}`)
        if (cancelled) return
        const data: DraftResponse | null = res.ok ? await res.json() : null
        if (cancelled) return
        setActiveDraftContent(data?.content ?? null)
        if (data) {
          draftVersionRef.current = data.version
          setDocuments(prev => prev.map(d =>
            d.id === data.id ? { ...d, title: data.title } : d
          ))
        }
        setWorkbenchKey(k => k + 1)
      } catch {
        if (cancelled) return
        setActiveDraftContent(null)
        setWorkbenchKey(k => k + 1)
      }

      // 3. If no cached audit, try loading from backend
      if (!cached) {
        try {
          const auditRes = await apiFetch(`/api/drafts/${activeDocId}/audit`)
          if (cancelled) return
          if (auditRes.ok) {
            const auditData: AuditStatusResponse = await auditRes.json()
            const atomicBreakdown = mapClaimsToBreakdown(auditData.claims)
            const restoredResult = {
              original_text: auditData.audit.document_text_snapshot,
              atomic_breakdown: atomicBreakdown,
              abbreviations: auditData.audit.abbreviations ?? [],
              query: auditData.audit.document_text_snapshot,
            }
            setIsIncrementalResult(!!auditData.audit.content_json)
            setApiAuditResult(restoredResult)
            setHasAuditResults(true)
            setLastCompletedAuditId(auditData.audit.id)
            // Write to cache for future tab switches
            auditCacheRef.current.set(activeDocId, {
              apiAuditResult: restoredResult,
              hasAuditResults: true,
              auditError: null,
              lastCompletedAuditId: auditData.audit.id,
              isIncrementalResult: !!auditData.audit.content_json,
            })
          }
          // 404 = no audit exists — stay in "Ready to Audit" state (no-op)
        } catch {
          // Non-fatal: audit just won't be restored
        }
      }
    })()

    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDocId])

  // Debounced auto-save with optimistic concurrency.
  //
  // The flow:
  //   1. Bundle pending content/title into a PATCH body.
  //   2. Attach ``expected_version`` from draftVersionRef (set on
  //      the last GET/PATCH response).
  //   3. On 200, update draftVersionRef to the row the server returned.
  //   4. On 409, the server returns the *current* row in body.current.
  //      We show a toast offering "Reload" — accepting that overwrites
  //      the editor with the server's content and re-arms saves with
  //      the fresh version. Declining keeps the user's edits in memory;
  //      the next PATCH (without a version token) would clobber.
  //
  // This is "detect, don't merge" — the minimum to avoid silent data
  // loss without committing to CRDT plumbing. See migration
  // 20260522000001_drafts_collab_safety.sql.
  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(async () => {
      const docId = activeDocIdRef.current
      if (!docId) return
      const body: Record<string, unknown> = {}
      if (pendingContentRef.current) {
        body.content = pendingContentRef.current
        pendingContentRef.current = null
      }
      if (pendingTitleRef.current !== null) {
        body.title = pendingTitleRef.current
        pendingTitleRef.current = null
      }
      if (Object.keys(body).length === 0) return

      if (draftVersionRef.current !== null) {
        body.expected_version = draftVersionRef.current
      }

      setSaveStatus('saving')
      try {
        const res = await apiFetch(`/api/drafts/${docId}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        })
        if (res.ok) {
          const updated: DraftResponse = await res.json()
          if (docId === activeDocIdRef.current) {
            draftVersionRef.current = updated.version
          }
          setSaveStatus('saved')
        } else if (res.status === 409) {
          // Another session saved this draft. Re-fetch to learn the
          // current state, then show a non-blocking toast offering
          // reload. The user keeps their edits in memory until they
          // act.
          setSaveStatus('idle')
          let currentRow: DraftResponse | null = null
          try {
            const conflict = await res.json() as {
              detail?: { current?: DraftResponse } | string
            }
            const detail = conflict?.detail
            if (detail && typeof detail === 'object' && detail.current) {
              currentRow = detail.current
            }
          } catch {
            // Server didn't return a parseable body — fall back to GET below.
          }
          if (!currentRow) {
            try {
              const refetch = await apiFetch(`/api/drafts/${docId}`)
              if (refetch.ok) currentRow = await refetch.json() as DraftResponse
            } catch {
              // ignore
            }
          }
          if (docId === activeDocIdRef.current && currentRow) {
            const rowVersion = currentRow.version
            const rowContent = currentRow.content
            const rowTitle = currentRow.title
            toast.warning('This draft was updated in another session', {
              description: "Reload to see the latest version. Your unsaved changes will be lost.",
              duration: 15000,
              action: {
                label: 'Reload',
                onClick: () => {
                  if (activeDocIdRef.current !== docId) return
                  draftVersionRef.current = rowVersion
                  pendingContentRef.current = null
                  pendingTitleRef.current = null
                  setActiveDraftContent(rowContent ?? null)
                  setDocuments(prev =>
                    prev.map(d => d.id === docId ? { ...d, title: rowTitle } : d),
                  )
                  setWorkbenchKey(k => k + 1)
                },
              },
            })
          }
        } else {
          setSaveStatus('idle')
          toast.error('Failed to save draft')
        }
      } catch {
        setSaveStatus('idle')
        toast.error('Error saving draft — please check your connection')
      }
    }, 1000)
  }, [])

  const handleContentChange = useCallback(async (json: JSONContent) => {
    pendingContentRef.current = json

    // Auto-create a draft if the user starts typing without clicking "New Document"
    if (!activeDocIdRef.current && activeWorkspaceId && !autoCreatingRef.current) {
      autoCreatingRef.current = true
      try {
        const now = new Date().toISOString()
        const res = await apiFetch('/api/drafts', {
          method: 'POST',
          body: JSON.stringify({
            workspace_id: activeWorkspaceId,
            title: pendingTitleRef.current ?? 'Untitled',
            content: pendingContentRef.current,
          }),
        })
        if (res.ok) {
          const draft = await res.json()
          const title = pendingTitleRef.current ?? 'Untitled'
          setDocuments(prev => [{
            id: draft.id,
            title,
            lastModified: new Date(),
            createdAt: now,
            lastOpenedAt: now,
            workspaceId: activeWorkspaceId,
          }, ...prev])
          skipReloadRef.current = true
          setActiveDocId(draft.id)
          setActiveNav('documents')
          capture('draft_created')
          // Content and title were already sent in the POST — clear refs
          pendingContentRef.current = null
          pendingTitleRef.current = null
        }
      } catch {
        // silent — user can still type, save will retry on next change
      } finally {
        autoCreatingRef.current = false
      }
      return
    }

    if (autoCreatingRef.current) return

    scheduleSave()
  }, [scheduleSave, activeWorkspaceId])

  const handleTitleChange = useCallback((title: string) => {
    pendingTitleRef.current = title
    setDocuments(prev => prev.map(d =>
      d.id === activeDocIdRef.current ? { ...d, title } : d
    ))
    scheduleSave()
  }, [scheduleSave])

  // Reset "Saved" indicator after 2s
  useEffect(() => {
    if (saveStatus !== 'saved') return
    const t = setTimeout(() => setSaveStatus('idle'), 2000)
    return () => clearTimeout(t)
  }, [saveStatus])

  // Clean up save timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    }
  }, [])

  const sourceIdsWithClaims = useMemo(() => {
    const ids = new Set<string>()
    if (apiAuditResult?.atomic_breakdown) {
      for (const entry of apiAuditResult.atomic_breakdown) {
        for (const ev of entry.evidences ?? []) {
          if (ev.source_doc) ids.add(ev.source_doc)
        }
      }
    }
    return ids
  }, [apiAuditResult])

  const auditingDraftIds = useMemo(() => new Set(pollingAudits.keys()), [pollingAudits])

  const filteredDocs = documents.filter((d) => d.workspaceId === (activeWorkspaceId ?? ''))
  const filteredLibrary = libraryItems.filter((l) =>
    l.workspaceId === (activeWorkspaceId ?? '') || l.workspaceId === ''
  )
  const selectedLibraryItem = libraryItems.find((l) => l.id === selectedLibraryItemId) ?? null

  const handleLibraryUpload = useCallback((item: LibraryItem, replaceId?: string) => {
    setLibraryItems((prev) => {
      const targetId = replaceId ?? item.id
      const existing = prev.find((i) => i.id === targetId)
      let updated: LibraryItem[]
      if (existing) {
        updated = prev.map((i) => (i.id === targetId ? item : i))
      } else {
        updated = [...prev, item]
      }
      // Guarantee uniqueness by id (last wins)
      return [...new Map(updated.map((i) => [i.id, i])).values()]
    })
  }, [])

  const handleNavChange = useCallback((nav: ActiveNav) => {
    setActiveNav(nav)
  }, [])

  const handleClaimSelect = useCallback((claimId: string | null) => {
    setSelectedClaimId(claimId)
    setFocusedSourceId(null)
    if (claimId) {
      setRightPanelMode('review')
      setIsRightPanelOpen(true)
    }
  }, [])

  const handleCitationClick = useCallback((claimIndices: number[]) => {
    setFocusedClaimIndices(claimIndices)
    setRightPanelMode('review')
    setIsRightPanelOpen(true)
  }, [])

  const handleReviewClick = useCallback(() => {
    setRightPanelMode('review')
    setIsRightPanelOpen(true)
  }, [])

  const handleRunAudit = useCallback(async () => {
    if (!activeWorkspaceId) {
      toast.error('Please select a workspace first')
      return
    }

    const getText = editorGetTextRef.current
    if (!getText) return

    const query = getText().trim()
    if (!query) {
      toast.error('Please add some text to the editor first')
      return
    }

    setIsAuditing(true)
    setApiAuditResult(null)
    setFocusedClaimIndices([])
    setProgressMessage('Starting audit...')
    setAuditError(null)
    editorHelpersRef.current?.setEditable(false)

    try {
      // Auto-create a draft if the user started typing without creating a document
      let draftId = activeDocId
      if (!draftId) {
        try {
          const editorJson = editorHelpersRef.current?.getJSON()
          const draftRes = await apiFetch('/api/drafts', {
            method: 'POST',
            body: JSON.stringify({
              workspace_id: activeWorkspaceId,
              title: 'Untitled',
              ...(editorJson ? { content: editorJson } : {}),
            }),
          })
          if (draftRes.ok) {
            const draft = await draftRes.json()
            draftId = draft.id
            setActiveDocId(draftId)
          }
        } catch {
          // Fall through — backend will auto-create as well
        }
      } else {
        // Flush current editor content so the backend can hydrate citations
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
        const editorJson = editorHelpersRef.current?.getJSON()
        if (editorJson) {
          try {
            await apiFetch(`/api/drafts/${draftId}`, {
              method: 'PATCH',
              body: JSON.stringify({ content: editorJson }),
            })
          } catch { /* non-critical — backend will try to hydrate whatever is stored */ }
        }
      }

      const res = await apiFetch('/api/chat', {
        method: 'POST',
        body: JSON.stringify({
          draft_id: draftId ?? undefined,
          query,
          workspace_id: activeWorkspaceId,
        }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error((err as { detail?: string }).detail ?? `HTTP ${res.status}`)
      }
      const data: AuditInitResponse = await res.json()
      const auditDraftId = draftId!
      setPollingAudits(prev => new Map(prev).set(auditDraftId, data.audit_id))
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to start audit')
      setIsAuditing(false)
      setProgressMessage(null)
      editorHelpersRef.current?.setEditable(true)
    }
  }, [activeWorkspaceId, activeDocId])

  const handleRunIncrementalAudit = useCallback(async () => {
    if (!activeWorkspaceId || !activeDocId) {
      toast.error('Please select a workspace and document first')
      return
    }

    const helpers = editorHelpersRef.current
    if (!helpers) return

    const tiptapJson = helpers.getJSON()

    setIsAuditing(true)
    setHasAuditResults(false)
    setApiAuditResult(null)
    setFocusedClaimIndices([])
    setProgressMessage('Starting incremental audit...')
    setAuditError(null)

    helpers.setEditable(false)

    try {
      const res = await apiFetch('/api/audits/incremental', {
        method: 'POST',
        body: JSON.stringify({
          draft_id: activeDocId,
          tiptap_json: tiptapJson,
        }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error((err as { detail?: string }).detail ?? `HTTP ${res.status}`)
      }
      const data: AuditInitResponse = await res.json()
      setPollingAudits(prev => new Map(prev).set(activeDocId, data.audit_id))
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to start incremental audit')
      setIsAuditing(false)
      setProgressMessage(null)
      helpers.setEditable(true)
    }
  }, [activeWorkspaceId, activeDocId])

  const handleAuditComplete = useCallback(() => {
    setIsAuditing(false)
    setHasAuditResults(true)
    setHighlightsVisible(true)
  }, [])

  const handleSourceReject = useCallback((_claimId: string, _sourceId: string, _page: number) => {}, [])

  const handleApplyEdit = useCallback((originalText: string, newText: string): boolean => {
    return editorHelpersRef.current?.replaceText(originalText, newText) ?? false
  }, [])


  const handleOpenDetails = useCallback((id: string) => {
    setSelectedLibraryItemId(id)
    setRightPanelMode('details')
    setIsRightPanelOpen(true)
  }, [])

  const handleToggleRag = useCallback(async (id: string, isActive: boolean) => {
    // Optimistic update
    setLibraryItems(prev => prev.map(item =>
      item.id === id ? { ...item, isActiveInRag: isActive } : item
    ))
    try {
      const res = await apiFetch(`/api/sources/${id}/rag-toggle?workspace_id=${activeWorkspaceId}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: isActive }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
    } catch {
      // Revert on failure
      setLibraryItems(prev => prev.map(item =>
        item.id === id ? { ...item, isActiveInRag: !isActive } : item
      ))
      toast.error('Failed to update archive setting.')
    }
  }, [activeWorkspaceId])

  const handleDeleteLibraryItem = useCallback(async (id: string) => {
    try {
      const res = await apiFetch(`/api/sources/${id}?workspace_id=${activeWorkspaceId}`, { method: 'DELETE' })
      if (!res.ok) {
        if (res.status === 403) {
          const data = await res.json().catch(() => null)
          const msg = data?.detail ?? 'Cannot remove source: This document is currently linked to active claims in this workspace. Please remove the claims first to maintain audit traceability.'
          toast.error(msg)
          return
        }
        throw new Error(`HTTP ${res.status}`)
      }
      setLibraryItems(prev => prev.filter(item => item.id !== id))

      // Re-fetch audit results so claims whose source was just deleted
      // flip to unverified in the UI without a page refresh
      if (lastCompletedAuditId) {
        try {
          const auditRes = await apiFetch(`/api/audits/${lastCompletedAuditId}`)
          if (auditRes.ok) {
            const data: AuditStatusResponse = await auditRes.json()
            const atomicBreakdown = mapClaimsToBreakdown(data.claims)
            const query = editorGetTextRef.current?.() ?? ''
            setApiAuditResult({
              original_text: data.audit.document_text_snapshot,
              atomic_breakdown: atomicBreakdown,
              abbreviations: data.audit.abbreviations ?? [],
              query,
            })
          }
        } catch {
          // Non-fatal: UI just won't auto-refresh
        }
      }
    } catch (err) {
      console.error('Failed to delete source:', err)
      toast.error('Failed to delete source. Please try again.')
    }
  }, [lastCompletedAuditId, activeWorkspaceId])

  const handleReprocessMetadata = useCallback(async (sourceId: string) => {
    try {
      const res = await apiFetch(`/api/sources/${sourceId}/reprocess?workspace_id=${activeWorkspaceId}`, { method: 'POST' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      setLibraryItems(prev => prev.map(item =>
        item.id === sourceId ? { ...item, status: 'processing' as const } : item
      ))
      toast.info('Restarting metadata enrichment...')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reprocess metadata.')
    }
  }, [activeWorkspaceId])

  const handleUpdateCitation = useCallback(async (
    sourceId: string,
    changes: { amaOverride?: string | null; shortOverride?: string | null }
  ) => {
    let prevItems: LibraryItem[] = []
    setLibraryItems(prev => {
      prevItems = prev
      return prev.map(item => {
        if (item.id !== sourceId) return item
        const updated = { ...item }
        if (changes.amaOverride !== undefined) {
          updated.amaCitationOverride = changes.amaOverride ?? undefined
        }
        if (changes.shortOverride !== undefined) {
          updated.shortCitationOverride = changes.shortOverride ?? undefined
        }
        // Recompute the displayed title so library card and details header
        // reflect the override (title fell back to plain ama/short before).
        const effectiveShort = updated.shortCitationOverride ?? updated.shortCitation
        const effectiveAma = updated.amaCitationOverride ?? updated.amaCitation
        updated.title = effectiveShort ?? effectiveAma ?? updated.title
        return updated
      })
    })

    try {
      const body: { ama_override?: string | null; short_override?: string | null } = {}
      if (changes.amaOverride !== undefined) body.ama_override = changes.amaOverride
      if (changes.shortOverride !== undefined) body.short_override = changes.shortOverride

      const res = await apiFetch(`/api/sources/${sourceId}/citation-override`, {
        method: 'PUT',
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        const errBody = await res.json().catch(() => null)
        const detail = errBody?.detail ?? `HTTP ${res.status}`
        throw new Error(detail)
      }

      const isRestore = changes.amaOverride === null || changes.shortOverride === null
      toast.success(isRestore ? 'AI citation restored' : 'Citation updated')
    } catch (err) {
      setLibraryItems(prevItems)
      const msg = err instanceof Error ? err.message : 'Failed to update citation'
      toast.error(`Failed to update citation: ${msg}`)
      console.error('[citation-override] update failed:', err)
    }
  }, [])

  const handleNewDocument = useCallback(async () => {
    if (!activeWorkspaceId) {
      toast.error('Please select a workspace first')
      return
    }
    const workspaceId = activeWorkspaceId
    const now = new Date().toISOString()
    let id: string
    try {
      const res = await apiFetch('/api/drafts', {
        method: 'POST',
        body: JSON.stringify({ workspace_id: workspaceId, title: 'Untitled' }),
      })
      if (res.ok) {
        const draft = await res.json()
        id = draft.id
      } else {
        id = crypto.randomUUID()
      }
    } catch {
      id = crypto.randomUUID()
    }
    setDocuments(prev => [{
      id,
      title: 'Untitled',
      lastModified: new Date(),
      createdAt: now,
      lastOpenedAt: now,
      workspaceId: activeWorkspaceId ?? '',
    }, ...prev])
    setActiveDocId(id)
    setActiveNav('documents')
  }, [activeWorkspaceId])

  const handleRenameWorkspace = useCallback(async (name: string) => {
    if (!activeWorkspaceId) return
    try {
      await renameWorkspace(activeWorkspaceId, name)
      toast.success('Workspace renamed')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to rename workspace')
    }
  }, [activeWorkspaceId, renameWorkspace])

  const handleOpenWsSettings = useCallback(() => {
    setWsSettingsOpen(true)
  }, [])

  return (
    <TooltipProvider>
      <Toaster position="bottom-right" richColors />
      <div className="flex h-screen w-screen overflow-hidden bg-background">
        {/* Column A: Main Sidebar */}
        <Sidebar
          workspaces={workspaces}
          currentWorkspaceId={activeWorkspaceId ?? ''}
          onWorkspaceChange={setActiveWorkspaceId}
          activeNav={activeNav}
          onNavChange={handleNavChange}
          collapsed={isSidebarCollapsed}
          onToggleCollapse={() => setIsSidebarCollapsed((prev) => !prev)}
          onOpenSettings={() => setSettingsOpen(true)}
          onOpenInvite={() => setInviteOpen(true)}
          onCreateWorkspace={createWorkspace}
          onOpenWorkspaceSettings={() => handleOpenWsSettings()}
          onLogout={logout}
          onNewDocument={handleNewDocument}
          activeWorkspaceId={activeWorkspaceId}
          hasLibrarySources={libraryItems.length > 0}
          user={user}
        />

        {/* Column A.5: Sub-Sidebar (conditional) */}
        <SubSidebar
          activeNav={activeNav}
          onClose={() => setActiveNav(null)}
          documents={filteredDocs}
          activeDocId={activeDocId}
          onDocSelect={setActiveDocId}
          libraryItems={filteredLibrary}
          supersededItems={supersededItems}
          onUploadClick={() => setUploadOpen(true)}
          onOpenDetails={handleOpenDetails}
          onDeleteItem={handleDeleteLibraryItem}
          onToggleRag={handleToggleRag}
          activeWorkspaceId={activeWorkspaceId}
          sourceIdsWithClaims={sourceIdsWithClaims}
          auditingDraftIds={auditingDraftIds}
        />

        {/* Column B: Workbench (or Welcome gate) */}
        {activeWorkspaceId ? (
          <>
            <Workbench
              key={workbenchKey}
              onClaimSelect={handleClaimSelect}
              onReviewClick={handleReviewClick}
              isAuditing={isAuditing}
              onAuditComplete={handleAuditComplete}
              highlightsVisible={highlightsVisible}
              onOpenSettings={() => setSettingsOpen(true)}
              focusedSourceId={focusedSourceId}
              onEditorReady={(helpers) => {
                editorHelpersRef.current = helpers
                editorGetTextRef.current = helpers.getText
                // Lock editor if this draft has a running audit
                if (activeDocId && pollingAudits.has(activeDocId)) {
                  helpers.setEditable(false)
                }
              }}
              apiAuditResult={apiAuditResult}
              isIncrementalResult={isIncrementalResult}
              onCitationClick={handleCitationClick}
              progressMessage={progressMessage}
              userEmail={user?.email}
              currentUserId={user?.id ?? null}
              onLogout={logout}
              initialContent={activeDraftContent}
              initialTitle={documents.find(d => d.id === activeDocId)?.title ?? 'Untitled'}
              onContentChange={handleContentChange}
              onTitleChange={handleTitleChange}
              saveStatus={saveStatus}
              sourceLookup={sourceLookup}
              onActiveIndicesChange={setActiveBreakdownIndices}
              activeSurgeryClaimIndex={activeSurgeryClaimIndex}
              claimStatusOverrides={claimStatusOverrides}
              hiddenSourcesByClaimUuid={hiddenSourcesByClaimUuid}
              onStaleChange={setIsStale}
              draftId={activeDocId}
              onEditAbbreviations={() => {
                setRightPanelMode('review')
                setIsRightPanelOpen(true)
                setReviewViewMode('abbreviations')
              }}
            />

            {/* Column C: Right Panel — provider always mounted so review_state
                rehydration (hidden_source_ids → labels/bibliography) runs even
                when the panel is closed at reload. RightPanel itself stays
                conditional on isRightPanelOpen. */}
            <ReviewPanelProvider
              apiAuditResult={apiAuditResult}
              sourceLookup={sourceLookup}
              isAuditing={isAuditing}
              hasAuditResults={hasAuditResults}
              progressMessage={progressMessage}
              auditError={auditError}
              activeDocumentId={activeDocumentId}
              selectedClaimId={selectedClaimId}
              setSelectedClaimId={setSelectedClaimId}
              focusedClaimIndices={focusedClaimIndices}
              setFocusedClaimIndices={setFocusedClaimIndices}
              activeSourceId={focusedSourceId}
              setActiveSourceId={setFocusedSourceId}
              activeBreakdownIndices={activeBreakdownIndices}
              onRunAudit={handleRunAudit}
              onRunIncrementalAudit={handleRunIncrementalAudit}
              onHighlightClause={setFocusedSourceId}
              onSourceReject={handleSourceReject}
              onReprocessMetadata={handleReprocessMetadata}
              onUpdateCitation={handleUpdateCitation}
              onImportToLibrary={handleLibraryUpload}
              applyEdit={handleApplyEdit}
              viewMode={reviewViewMode}
              setViewMode={setReviewViewMode}
              surgeryClaimId={surgeryClaimId}
              setSurgeryClaimId={setSurgeryClaimId}
              abbreviations={apiAuditResult?.abbreviations ?? []}
              onAbbreviationsChange={(next) => {
                setApiAuditResult((prev) => (prev ? { ...prev, abbreviations: next } : prev))
              }}
              draftId={activeDocId}
              onClaimStatusChange={setClaimStatusOverrides}
              onHiddenSourcesChange={setHiddenSourcesByClaimUuid}
              onRemoveCitationFromDoc={(claimUuid) =>
                editorHelpersRef.current?.removeCitationByClaimUuid?.(claimUuid) ?? null
              }
              onRestoreCitationToDoc={(snap) => editorHelpersRef.current?.insertCitationAt(
                Math.max(0, snap.originalPos),
                { claimId: snap.claimId, attachmentId: snap.attachmentId, sourceUuids: snap.sourceUuids, indices: snap.indices, claimIndices: snap.claimIndices, sentenceHash: snap.sentenceHash },
              ) ?? false}
              lastCompletedAuditId={lastCompletedAuditId}
              isStale={isStale}
            >
              {isRightPanelOpen && (
                <RightPanel
                  mode={rightPanelMode}
                  selectedLibraryItem={selectedLibraryItem}
                  onClose={() => setIsRightPanelOpen(false)}
                  highlightsVisible={highlightsVisible}
                  onToggleHighlights={() => setHighlightsVisible((prev) => !prev)}
                />
              )}
            </ReviewPanelProvider>
          </>
        ) : (
          <WorkspaceWelcome />
        )}
      </div>

      {/* Modals */}
      <UploadModal
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onUpload={handleLibraryUpload}
        onSetActiveDocumentId={setActiveDocumentId}
        workspaceId={activeWorkspaceId}
        onRefreshLibrary={refreshLibrary}
      />
      <SettingsModal
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        user={user}
        onAccountDeleted={logout}
        onOpenInvite={() => {
          setSettingsOpen(false)
          setInviteOpen(true)
        }}
      />
      <WorkspaceSettingsModal
        open={wsSettingsOpen}
        onOpenChange={setWsSettingsOpen}
        workspaceName={workspaces.find((ws) => ws.id === activeWorkspaceId)?.name ?? ''}
        onRenameWorkspace={handleRenameWorkspace}
      />
      <InviteModal open={inviteOpen} onOpenChange={setInviteOpen} user={user} />
    </TooltipProvider>
  )
}

// ── Root app (handles auth gates + workspace provider) ───────────────────────

/** Pay-first paywall gate, between auth and the app shell. Resolves the org
 *  once (shared `useOrgInfo` cache — components inside reuse it, so this adds
 *  no extra request) and swaps in the SubscribeGate screen when the backend
 *  402s (valid login, no subscription). Sits OUTSIDE WorkspaceProvider so an
 *  unsubscribed user triggers no org-scoped fetches (which would all 402). */
function SubscriptionGate({ logout, children }: { logout: () => void; children: ReactNode }) {
  const { loading, subscriptionRequired } = useOrgInfo()
  if (loading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }
  if (subscriptionRequired) return <SubscribeGate onSignOut={logout} />
  return <>{children}</>
}

function App() {
  const { user, isAuthenticated, isLoading: isAuthLoading, authError, login, logout } = useAuth()

  // True only right after a logout that could NOT complete a WorkOS hosted
  // logout (no session id, or /auth/logout unreachable). The SSO cookie may
  // still be live in that case, so auto-redirecting to /authorize would
  // silently sign the user back in — instead we render an explicit "Sign in"
  // screen. One-shot, read once from sessionStorage at mount.
  const [signedOut, setSignedOut] = useState(() => hasIntentionalLogout())

  // Auto-redirect to WorkOS AuthKit when unauthenticated. We skip the redirect
  // when (a) the auth hook is still processing, (b) we're mid-callback (?code
  // in URL), (c) the last login attempt errored — the user clicks "Try again"
  // to retry rather than looping — or (d) the user just intentionally signed
  // out (see `signedOut`), so we don't bounce them straight back in.
  useEffect(() => {
    if (isAuthLoading || isAuthenticated || authError || signedOut) return
    const hasOAuthCode = new URLSearchParams(window.location.search).has('code')
    if (hasOAuthCode) return
    login()
  }, [isAuthLoading, isAuthenticated, authError, signedOut, login])

  const handleSignIn = useCallback(() => {
    clearIntentionalLogout()
    setSignedOut(false)
    login()
  }, [login])

  if (!isAuthenticated) {
    if (signedOut) {
      return (
        <div className="flex h-screen w-screen items-center justify-center bg-background">
          <div className="flex flex-col items-center gap-4 text-center max-w-sm px-6">
            <p className="text-sm text-muted-foreground">You've been signed out.</p>
            <Button
              className="bg-pharma-primary hover:bg-pharma-primary/90 text-white"
              onClick={handleSignIn}
            >
              Sign in
            </Button>
          </div>
        </div>
      )
    }
    if (authError) {
      return (
        <div className="flex h-screen w-screen items-center justify-center bg-background">
          <div className="flex flex-col items-center gap-4 text-center max-w-sm px-6">
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2 w-full">
              {authError}
            </p>
            <Button
              className="bg-pharma-primary hover:bg-pharma-primary/90 text-white"
              onClick={login}
            >
              Try again
            </Button>
          </div>
        </div>
      )
    }
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <SubscriptionGate logout={logout}>
      <WorkspaceProvider>
        <AuthenticatedApp
          user={user}
          logout={logout}
          isAuthenticated={isAuthenticated}
        />
      </WorkspaceProvider>
    </SubscriptionGate>
  )
}

export default App
